import React, { ReactNode, useContext, useEffect, useState } from 'react';
import { useTransitionState } from 'react-transition-state';

import { SideDrawerControllerContextType, TransitionState } from './types';

type SideDrawerControllerProps = {
  isOpen?: boolean;
  onClose: () => void;
  children: ReactNode;
};

const SideDrawerControllerContext =
  React.createContext<null | SideDrawerControllerContextType>(null);

export const SideDrawerControllerContextProvider =
  SideDrawerControllerContext.Provider;

export const useSideDrawerControllerContext = () => {
  let context = useContext(SideDrawerControllerContext);
  if (!context) {
    throw new Error(
      'SideDrawer must be wrapped in a <SideDrawerController>. You should generally do this outside of the component that renders the <SideDrawer>. See https://balance.reckon.com/package/side-drawer#sidedrawercontroller for how to use it and why.'
    );
  }

  return context;
};

export const SideDrawerController = ({
  isOpen = false,
  onClose,
  children,
}: SideDrawerControllerProps) => {
  const [animationTimeout, setAnimationTimeout] = useState(150);

  const [transitionState, toggle] = useTransitionState({
    timeout: animationTimeout,
    initialEntered: isOpen,
    mountOnEnter: true,
    unmountOnExit: true,
  });

  // Update transition timeout when animationTimeout changes
  useEffect(() => {
    // If currently transitioning and timeout changed, restart transition
    if (
      transitionState.status === 'entering' ||
      transitionState.status === 'exiting'
    ) {
      toggle(isOpen);
    }
  }, [animationTimeout, isOpen, toggle, transitionState.status]);

  // Sync the transition state with the isOpen prop
  useEffect(() => {
    toggle(isOpen);
  }, [isOpen, toggle]);

  // Only render children if the component is mounted
  if (!transitionState.isMounted) {
    return null;
  }

  // Map new transition states to legacy states for backward compatibility
  const mapTransitionState = (
    status: typeof transitionState.status
  ): TransitionState => {
    switch (status) {
      case 'preEnter':
        return 'entering';
      case 'preExit':
        return 'exiting';
      default:
        return status as TransitionState;
    }
  };

  return (
    <SideDrawerControllerContextProvider
      value={{
        onClose,
        transitionState: mapTransitionState(transitionState.status),
        animationTimeout,
        setAnimationTimeout,
      }}
    >
      {children}
    </SideDrawerControllerContextProvider>
  );
};
